// TODO: Remove when we deploy GovCloud to Commercial
/* eslint-disable @typescript-eslint/no-unused-vars */
import { LoadingSpinner } from '@gamma/ui/progress';
import { Auth } from 'aws-amplify';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useGetUserTcAcceptance } from '../features/user/queries';
import { useLogOperationCall } from '../shared/hooks';
import {
  AppConfigurationsProvider,
  AuthContext,
  DateRangeProvider,
  useHumioToken,
  useLogScale,
  useTermsAcceptance,
} from '../shared/providers';
import { AuthState } from '../shared/types/auth-types';
import { AppRoutes } from './AppRoutes';
import { isGovCloudRegion, getCognitoRegion } from '../shared/utils/isGovCloud';
import { SystemUseBanner } from '../features/user/components/SystemUseBanner';
import { IdleLogout } from './IdleLogout';

type InitState = {
  isInitialized: boolean;
  isInitializing: boolean;
  lastInitTime: number;
  lastInitUser: string | null;
  initAttempts: number;
  lastError: Error | null;
  eulaCheckComplete: boolean;
};

export const App = React.memo(() => {
  const {
    user,
    userRole,
    setUserAlias,
    userLicense,
    userLicenseLoading,
    authState,
  } = useContext(AuthContext);

  const { token: humioToken, refreshToken } = useHumioToken();
  const { forceRefreshDashboards } = useLogScale();
  const { acceptTermsIfNeeded } = useTermsAcceptance();
  const logOperationCall = useLogOperationCall();

  const [getUserTcAcceptance] = useGetUserTcAcceptance();

  const [preferredMFA, setPreferredMFA] = useState<string>('');
  const isSSOLogin = sessionStorage.getItem('isSSOLogin') === 'true';

  // Compute token readiness early
  const hasValidIdToken = Boolean(user?.signInUserSession?.idToken?.jwtToken);

  const initializationRef: MutableRefObject<InitState> = useRef<InitState>({
    isInitialized: false,
    isInitializing: false,
    lastInitTime: 0,
    lastInitUser: null,
    initAttempts: 0,
    lastError: null,
    eulaCheckComplete: false,
  });

  const [isAppReady, setIsAppReady] = useState(false);
  const userId: string = user?.attributes?.email || user?.attributes?.sub || '';

  const checkSSOUserEulaStatus = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await getUserTcAcceptance();
      const isEulaAccepted = Boolean(data?.getUserTcAcceptance?.body?.data);
      localStorage.setItem('isEulaAccepted', String(isEulaAccepted));
      return isEulaAccepted;
    } catch {
      return false;
    }
  }, [getUserTcAcceptance]);

  const initializeApp = useCallback(async () => {
    const now = Date.now();
    const init = initializationRef.current;

    // Debounce repeated init for same user within 30s
    if (
      init.isInitialized &&
      init.lastInitUser === userId &&
      now - init.lastInitTime < 30_000
    ) {
      setIsAppReady(true);
      return;
    }

    if (init.isInitializing) return;

    if (init.initAttempts > 5) {
      setIsAppReady(true);
      return;
    }

    if (
      !user ||
      !userId ||
      authState !== AuthState.SignedIn ||
      !userLicense ||
      userLicenseLoading ||
      !userLicense.tenant_type
    ) {
      return;
    }

    init.isInitializing = true;
    init.initAttempts += 1;

    try {
      // Set alias if present
      if (user.attributes?.['custom:alias'] && setUserAlias) {
        setUserAlias(user.attributes['custom:alias']);
      }

      const idToken = user.signInUserSession?.idToken?.jwtToken;
      if (!idToken) {
        throw new Error('No ID token available');
      }

      // Ensure Humio token
      if (!humioToken) {
        await refreshToken(false);
      }

      // Handle SSO users: one-time EULA check
      if (isSSOLogin && !init.eulaCheckComplete) {
        const isEulaAccepted = await checkSSOUserEulaStatus();
        init.eulaCheckComplete = true;

        if (!isEulaAccepted) {
          setIsAppReady(true);
          localStorage.setItem('accept_privacy_policy', 'true');
          localStorage.setItem('humio_tc_accepted', 'true');
          init.isInitialized = false;
          return;
        }
      }

      // Accept terms if needed
      const termsResult = await acceptTermsIfNeeded({
        idToken,
        sessionToken: humioToken,
        source: 'App.initializeApp',
        forceAccept: false,
      });

      if (termsResult.success && termsResult.eulaAccepted) {
        try {
          await logOperationCall('PRIVACY_POLICY_ACKNOWLEDGED', 'USER');
        } catch {
          // ignore best-effort logging failure
        }
      }

      // Optionally refresh LogScale dashboards
      if (humioToken && forceRefreshDashboards) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          await forceRefreshDashboards();
        } catch {
          // ignore non-critical dashboard refresh issues
        }
      }

      // Mark initialized
      init.isInitialized = true;
      init.lastInitTime = Date.now();
      init.lastInitUser = userId;
      init.lastError = null;
      setIsAppReady(true);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      init.lastError = err;

      const tooManyAttempts = err.message.includes(
        'Too many token generation attempts',
      );

      // Backoff timers (don't block UI)
      setTimeout(
        () => {
          init.isInitializing = false;
          if (tooManyAttempts) init.initAttempts = 0; // reset after long backoff
        },
        tooManyAttempts ? 30_000 : 5_000,
      );

      setIsAppReady(true);
    } finally {
      if (
        !initializationRef.current.lastError?.message?.includes(
          'Too many token generation attempts',
        )
      ) {
        initializationRef.current.isInitializing = false;
      }
    }
  }, [
    user,
    userId,
    authState,
    userLicense,
    userLicenseLoading,
    setUserAlias,
    acceptTermsIfNeeded,
    humioToken,
    refreshToken,
    logOperationCall,
    forceRefreshDashboards,
    isSSOLogin,
    checkSSOUserEulaStatus,
  ]);

  // Kick off initialization once the minimum prerequisites are satisfied
  useEffect(() => {
    const init = initializationRef.current;
    if (
      authState === AuthState.SignedIn &&
      user &&
      userId &&
      !userLicenseLoading &&
      userLicense?.tenant_type &&
      !init.isInitialized &&
      !init.isInitializing
    ) {
      const timer = setTimeout(() => {
        void initializeApp();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    authState,
    user,
    userId,
    userLicenseLoading,
    userLicense?.tenant_type,
    initializeApp,
  ]);

  // If user is SSO and flags indicate pending acceptance, re-run initialize
  useEffect(() => {
    if (!isSSOLogin || !user || authState !== AuthState.SignedIn) return;

    const pendingEula =
      localStorage.getItem('accept_privacy_policy') === 'true';
    const pendingHumio = localStorage.getItem('humio_tc_accepted') === 'true';

    if (pendingEula || pendingHumio) {
      const init = initializationRef.current;
      if (!init.isInitializing && !init.isInitialized) {
        void initializeApp();
      }
    }
  }, [isSSOLogin, user, authState, initializeApp]);

  // Periodic token health check (silent)
  useEffect(() => {
    if (!user || authState !== AuthState.SignedIn) return;

    const checkTokenHealth = async () => {
      try {
        await Auth.currentSession();
      } catch {
        // rely on other listeners/links to recover
      }
    };

    const interval = setInterval(checkTokenHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, authState]);

  // If user identity changes, reset init state
  useEffect(() => {
    const init = initializationRef.current;
    if (userId && init.lastInitUser && userId !== init.lastInitUser) {
      init.isInitialized = false;
      init.isInitializing = false;
      init.lastInitTime = 0;
      init.initAttempts = 0;
      init.lastError = null;
      init.eulaCheckComplete = false;
      setIsAppReady(false);
    }
  }, [userId]);

  // System use notification banner logic
  const [bannerAcknowledged, setBannerAcknowledged] = useState(() => {
    return sessionStorage.getItem('systemUseBannerAcknowledged') === 'true';
  });

  const region = getCognitoRegion();
  const showSystemUseBanner = isGovCloudRegion(region) && !bannerAcknowledged;

  const handleAcknowledge = () => {
    sessionStorage.setItem('systemUseBannerAcknowledged', 'true');
    setBannerAcknowledged(true);
  };

  // Gate: Prevent routes from rendering until an ID token exists (avoids 401 bursts)
  if (authState === AuthState.SignedIn && !hasValidIdToken) {
    return <LoadingSpinner />;
  }

  // Gate: Boot/initialization still pending
  if (
    authState === AuthState.SignedIn &&
    !isAppReady &&
    !initializationRef.current.isInitialized
  ) {
    return <LoadingSpinner />;
  }

  // Only mount AppConfigurationsProvider once authenticated with a valid token.
  return (
    <DateRangeProvider>
      {/* System Use Notification Modal (GovCloud only, banner enabled) */}
      {showSystemUseBanner && (
        <SystemUseBanner
          isOpen={showSystemUseBanner}
          onAcknowledge={handleAcknowledge}
        />
      )}
      {authState === AuthState.SignedIn && hasValidIdToken ? (
        isGovCloudRegion(region) ? (
          <>
            <IdleLogout />
            <AppConfigurationsProvider>
              <AppRoutes
                user={user}
                isAdmin={userRole === 'admin'}
                isSSOLogin={isSSOLogin}
                preferredMFA={preferredMFA}
                setPreferredMFA={setPreferredMFA}
                tokenReady={hasValidIdToken}
              />
            </AppConfigurationsProvider>
          </>
        ) : (
          <AppConfigurationsProvider>
            <AppRoutes
              user={user}
              isAdmin={userRole === 'admin'}
              isSSOLogin={isSSOLogin}
              preferredMFA={preferredMFA}
              setPreferredMFA={setPreferredMFA}
              tokenReady={hasValidIdToken}
            />
          </AppConfigurationsProvider>
        )
      ) : (
        <AppRoutes
          user={user}
          isAdmin={userRole === 'admin'}
          isSSOLogin={isSSOLogin}
          preferredMFA={preferredMFA}
          setPreferredMFA={setPreferredMFA}
          tokenReady={false}
        />
      )}
    </DateRangeProvider>
  );
});

App.displayName = 'App';
